import { useEffect, useRef, useState } from 'react'

const DISMISS_KEY = 'doutian-webapk-install-dismissed'
const SESSION_KEY = 'doutian-webapk-install-shown'
const DISMISS_DAYS = 7
const MIN_DELAY_MS = 8000
const MAX_DELAY_MS = 18000
const VISIBLE_MS = 12000

function isAndroid() {
  return /Android/i.test(navigator.userAgent)
}

function isStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches
}

function readStorage(storage, key) {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, value)
  } catch {
    // Installation remains available when privacy settings block storage.
  }
}

export default function WebAPKInstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null)
  const [visible, setVisible] = useState(false)
  const showTimer = useRef(null)
  const hideTimer = useRef(null)

  useEffect(() => {
    const onBeforeInstall = (event) => {
      if (!isAndroid() || isStandaloneMode()) return

      event.preventDefault()
      setInstallEvent(event)

      const dismissedAt = Number(readStorage(localStorage, DISMISS_KEY) || 0)
      const canShow = Date.now() - dismissedAt > DISMISS_DAYS * 24 * 60 * 60 * 1000
      const shownThisSession = readStorage(sessionStorage, SESSION_KEY) === '1'
      if (!canShow || shownThisSession) return

      const delay = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS)
      showTimer.current = window.setTimeout(() => {
        writeStorage(sessionStorage, SESSION_KEY, '1')
        setVisible(true)
        hideTimer.current = window.setTimeout(() => setVisible(false), VISIBLE_MS)
      }, delay)
    }

    const onInstalled = () => {
      setVisible(false)
      setInstallEvent(null)
      try {
        localStorage.removeItem(DISMISS_KEY)
      } catch {
        // Ignore storage restrictions.
      }
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
      window.clearTimeout(showTimer.current)
      window.clearTimeout(hideTimer.current)
    }
  }, [])

  const dismiss = () => {
    setVisible(false)
    writeStorage(localStorage, DISMISS_KEY, String(Date.now()))
  }

  const install = async () => {
    if (!installEvent) return

    await installEvent.prompt()
    const choice = await installEvent.userChoice
    setVisible(false)
    setInstallEvent(null)

    if (choice.outcome !== 'accepted') {
      writeStorage(localStorage, DISMISS_KEY, String(Date.now()))
    }
  }

  if (!visible || !installEvent) return null

  return (
    <aside
      className="fixed bottom-4 left-3 right-3 z-[80] flex items-center gap-3 rounded-lg border border-blush/40 bg-white p-3 shadow-lg md:left-auto md:max-w-sm"
      aria-label="安装桌面应用提示"
    >
      <img src="/icons/icon-192.png" alt="" className="h-11 w-11 rounded-lg" />
      <div className="min-w-0 flex-1">
        <strong className="block text-sm text-cocoa">安装豆田造物</strong>
        <span className="block text-xs text-cocoa-light">添加到 Android 桌面，像应用一样打开</span>
      </div>
      <button
        type="button"
        onClick={install}
        className="rounded-md bg-blush px-3 py-2 text-xs font-semibold text-white"
      >
        安装
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="h-8 w-8 text-xl leading-none text-cocoa-light"
        aria-label="关闭安装提示"
      >
        ×
      </button>
    </aside>
  )
}
